const EVENT_TO_COUNTER = {
  view: "views",
  like: "likes",
  compare: "compares",
  cta_click: "ctaClicks"
};

const TREND_WEIGHTS = {
  views: 1,
  compares: 3,
  ctaClicks: 4,
  likes: 5
};

function createEmptyStats() {
  return {
    views: 0,
    likes: 0,
    compares: 0,
    ctaClicks: 0
  };
}

function createAnalyticsStore(seed) {
  const likedModelIds = Array.isArray(seed?.likedModelIds) ? seed.likedModelIds : [];
  return {
    models: seed?.models && typeof seed.models === "object" ? { ...seed.models } : {},
    likedModelIds: new Set(likedModelIds)
  };
}

function ensureModel(store, modelId) {
  const current = store.models[modelId];
  if (!current || typeof current !== "object") {
    store.models[modelId] = createEmptyStats();
  } else {
    store.models[modelId] = {
      views: Number(current.views) || 0,
      likes: Number(current.likes) || 0,
      compares: Number(current.compares) || 0,
      ctaClicks: Number(current.ctaClicks) || 0
    };
  }

  return store.models[modelId];
}

function recordEvent(store, eventType, modelId) {
  const counter = EVENT_TO_COUNTER[eventType];
  if (!counter || !modelId) return getModelStats(store, modelId);

  const stats = ensureModel(store, modelId);
  stats[counter] += 1;
  return getModelStats(store, modelId);
}

function toggleLike(store, modelId) {
  const stats = ensureModel(store, modelId);

  if (store.likedModelIds.has(modelId)) {
    store.likedModelIds.delete(modelId);
    stats.likes = Math.max(0, stats.likes - 1);
    return false;
  }

  store.likedModelIds.add(modelId);
  stats.likes += 1;
  return true;
}

function getModelStats(store, modelId) {
  return { ...ensureModel(store, modelId) };
}

function scoreStats(stats) {
  return (
    stats.views * TREND_WEIGHTS.views +
    stats.compares * TREND_WEIGHTS.compares +
    stats.ctaClicks * TREND_WEIGHTS.ctaClicks +
    stats.likes * TREND_WEIGHTS.likes
  );
}

function getTrendingModels(store, modelIds) {
  return modelIds
    .map((id) => {
      const stats = getModelStats(store, id);
      return { id, stats, score: scoreStats(stats), liked: store.likedModelIds.has(id) };
    })
    .sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
}

function serialiseStore(store) {
  return {
    models: store.models,
    likedModelIds: [...store.likedModelIds]
  };
}

function createPersistentAnalytics(storage, key = "tbh_scooter_analytics_v1") {
  let seed;
  try {
    seed = JSON.parse(storage.getItem(key) || "null");
  } catch {
    seed = null;
  }

  const store = createAnalyticsStore(seed);

  function persist() {
    storage.setItem(key, JSON.stringify(serialiseStore(store)));
  }

  return {
    store,
    record(eventType, modelId) {
      const stats = recordEvent(store, eventType, modelId);
      persist();
      return stats;
    },
    toggleLike(modelId) {
      const liked = toggleLike(store, modelId);
      persist();
      return liked;
    },
    trending(modelIds) {
      return getTrendingModels(store, modelIds);
    },
    stats(modelId) {
      return getModelStats(store, modelId);
    }
  };
}

const scooterCommunityAnalyticsApi = {
  createAnalyticsStore,
  recordEvent,
  toggleLike,
  getModelStats,
  getTrendingModels,
  createPersistentAnalytics
};

if (typeof module !== "undefined" && module.exports) {
  module.exports = scooterCommunityAnalyticsApi;
}

if (typeof window !== "undefined") {
  window.ScooterCommunityAnalytics = scooterCommunityAnalyticsApi;
}
